import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import timezoneSoft from "timezone-soft";

// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(utc);
// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(timezone);

export function isValidTimeZone(zone: string): boolean {
  try {
    Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Turns the time zone stored on a reservation into an IANA zone the browser
 * can format with, or `undefined` when it cannot be resolved.
 *
 * Reservations store the IANA zone, but the reservation edit form used to
 * store the abbreviation react-timezone-select displayed (e.g. "EDT", "CEST").
 * Abbreviations are ambiguous ("CST" is also China Standard Time), so they are
 * matched only against `pickerZones` - the zones the picker offers, in its
 * order - which is the list the abbreviation was taken from. Both the standard
 * and the daylight abbreviation match, so a reservation saved in summer still
 * resolves in winter.
 */
export function resolveTimeZone(
  value: string | null | undefined,
  pickerZones: readonly string[]
): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }

  if (
    (trimmed.includes("/") || pickerZones.includes(trimmed)) &&
    isValidTimeZone(trimmed)
  ) {
    return trimmed;
  }

  const abbreviation = trimmed.toUpperCase();
  for (const zone of pickerZones) {
    const info = timezoneSoft(zone)?.[0];
    const matches = [info?.standard?.abbr, info?.daylight?.abbr].some(
      (abbr) => abbr?.toUpperCase() === abbreviation
    );
    if (!matches) {
      continue;
    }

    // The abbreviations come from timezone-soft's zone, which can differ from
    // the picker key (the "GMT" key is Europe/London there, so BST matches it).
    const resolved = [info?.iana, zone].find(
      (candidate) => candidate && isValidTimeZone(candidate)
    );
    if (resolved) {
      return resolved;
    }
  }

  // Fixed zones such as "UTC" that the picker does not abbreviate.
  return isValidTimeZone(trimmed) ? trimmed : undefined;
}

/**
 * Formats a UTC media timestamp in the reservation's time zone, falling back
 * to the viewer's local time when the zone is unknown.
 */
export function formatInTimeZone(
  utcDate: string,
  format: string,
  timeZone?: string | null
): string {
  const date = dayjs.utc(utcDate);
  return (timeZone ? date.tz(timeZone) : date.local()).format(format);
}

/**
 * The date picker returns the chosen wall-clock time in the viewer's local
 * zone. Reinterprets that wall-clock time in the reservation's time zone so it
 * matches the timestamps shown in the gallery.
 */
export function wallClockInTimeZone(
  date: Date | null,
  timeZone?: string | null
): dayjs.Dayjs {
  if (!date || !timeZone) {
    return dayjs(date);
  }

  return dayjs.tz(dayjs(date).format("YYYY-MM-DDTHH:mm:ss"), timeZone);
}
